/**
 * @description
 * Filter panel component for configuring data stream filters.
 * Provides dynamic filter inputs based on selected table, time presets, and custom filters.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date October 8, 2025
 * @see admin/src/frontend/components/vscode-lens/FilterPanel.test.tsx
 */

import { useState } from 'react';
import { Filter, X } from 'lucide-react';
import type {
  FilterConfig,
  TimePreset,
  VSCodeLensTable,
  DomainAggregation,
  DomainFilterConfig
} from '../../../types/vscode-lens';
import { DomainFilter } from './DomainFilter';

interface FilterPanelProps {
  table: VSCodeLensTable | null;
  filters: FilterConfig;
  onChange: (filters: FilterConfig) => void;
  disabled?: boolean;
  domainAggregation?: DomainAggregation | null;
  domainFilterConfig?: DomainFilterConfig;
  onDomainFilterChange?: (config: DomainFilterConfig) => void;
}

const TIME_PRESETS: { value: TimePreset; label: string }[] = [
  { value: 'lastDay', label: 'Last Day' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

/**
 * Filter panel component
 */
export function FilterPanel({
  table,
  filters,
  onChange,
  disabled,
  domainAggregation,
  domainFilterConfig,
  onDomainFilterChange,
}: FilterPanelProps) {
  const [showCustomRange, setShowCustomRange] = useState(false);

  const handleTimePresetChange = (preset: TimePreset) => {
    onChange({
      ...filters,
      timePreset: preset,
      ...(preset !== 'custom' && { startTime: undefined, endTime: undefined }),
    });
    setShowCustomRange(preset === 'custom');
  };

  const handleColumnFilterChange = (key: string, value: string) => {
    onChange({
      ...filters,
      columnFilters: {
        ...filters.columnFilters,
        [key]: value,
      },
    });
  };

  const clearColumnFilter = (key: string) => {
    const newFilters = { ...filters.columnFilters };
    delete newFilters[key];
    onChange({
      ...filters,
      columnFilters: newFilters,
    });
  };

  const clearAllFilters = () => {
    onChange({
      timePreset: 'lastDay',
      batchSize: 100,
      columnFilters: {},
    });
    setShowCustomRange(false);
  };

  const hasActiveFilters = filters.timePreset !== 'lastDay' || 
    filters.batchSize !== 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm font-medium">
          <Filter className="h-4 w-4" />
          Filters
        </label>
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            disabled={disabled}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Time Range Filter */}
      <div>
        <label className="block text-sm font-medium mb-2">Time Range</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TIME_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => handleTimePresetChange(preset.value)}
              disabled={disabled}
              className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                filters.timePreset === preset.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-white border-input hover:bg-gray-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Custom Date Range */}
        {showCustomRange && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Start Time</label>
              <input
                type="datetime-local"
                value={filters.startTime || ''}
                onChange={(e) => onChange({ ...filters, startTime: e.target.value })}
                disabled={disabled}
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">End Time</label>
              <input
                type="datetime-local"
                value={filters.endTime || ''}
                onChange={(e) => onChange({ ...filters, endTime: e.target.value })}
                disabled={disabled}
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              />
            </div>
          </div>
        )}
      </div>

      {/* Table-Specific Filters - Hidden for initial load efficiency */}
      {/* Column filters are now handled client-side after data is loaded */}
      {/* 
      {table && (
        <div>
          <label className="block text-sm font-medium mb-2">Column Filters</label>
          <div className="space-y-2">
            // ... all the column filter inputs are commented out
          </div>
        </div>
      )}
      */}

      {/* Batch Size */}
      <div>
        <label className="block text-sm font-medium mb-2">Batch Size</label>
        <input
          type="number"
          min="10"
          max="1000"
          step="10"
          value={filters.batchSize || 100}
          onChange={(e) => onChange({ ...filters, batchSize: parseInt(e.target.value) || 100 })}
          disabled={disabled}
          className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Number of records to fetch per batch (10-1000)
        </p>
      </div>

      {/* Domain Filter - shown after data is loaded */}
      {domainAggregation && domainFilterConfig && onDomainFilterChange && (
        <DomainFilter
          domains={domainAggregation.domains}
          config={domainFilterConfig}
          onChange={onDomainFilterChange}
          emailColumns={domainAggregation.emailColumns}
          disabled={disabled}
        />
      )}
    </div>
  );
}

/**
 * Reusable filter input component
 */
function FilterInput({
  label,
  value,
  onChange,
  onClear,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          className="w-full rounded-md border border-input bg-white px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
        />
        {value && (
          <button
            onClick={onClear}
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */